import React, { useState } from "react";
import { Box, Typography, TextField, Button, IconButton, Paper, Stack, Collapse } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// This BuildingStep works with the meters-per-building structure:
// buildings = [{ building: { ...fields }, meters: [] }, ...]
const BuildingStep = ({
    buildings,
    setBuildings,
    errors // Receive errors prop
}) => {
    // Track which building panels are expanded
    const [expanded, setExpanded] = useState(buildings.map(() => true));

    // Add a new empty building (with meters array)
    const handleAddBuilding = () => {
        setBuildings([
            ...buildings,
            {
                building: {
                    name: "",
                    ean13: "",
                    postalCode: "",
                    streetNumber: "",
                    addition: "",
                    buildingYear: "",
                    floorSize: "",
                    isolationGrade: "",
                    energyLabel: "",
                    usage: "",
                    rental: "",
                    occupancyRate: "",
                    climateType: ""
                },
                meters: []
            }
        ]);
        setExpanded(prev => [...prev, true]);
    };

    // Remove a building by index
    const handleRemoveBuilding = (idx) => {
        setBuildings(buildings.filter((_, i) => i !== idx));
        setExpanded(expanded.filter((_, i) => i !== idx));
    };

    // Handle input change for a specific building
    const handleInputChange = (idx, e) => {
        const { name, value } = e.target;
        setBuildings(buildings.map((b, i) =>
            i === idx
                ? {
                    ...b,
                    building: {
                        ...b.building,
                        [name]: value
                    }
                }
                : b
        ));
    };

    // Toggle expand/collapse for a building
    const handleToggleExpand = (idx) => {
        setExpanded(expanded.map((val, i) => (i === idx ? !val : val)));
    };

    // Keep expanded state in sync if buildings array changes length
    React.useEffect(() => {
        if (expanded.length !== buildings.length) {
            setExpanded(buildings.map(() => true));
        }
    }, [buildings.length]);

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Enter Building Information</Typography>
            <Stack spacing={2}>
                {buildings.map((b, idx) => (
                    <Paper key={idx} sx={{ p: 2, position: "relative" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="subtitle1">
                                Building {idx + 1}: {b.building.name || <span style={{ color: "#aaa" }}>Unnamed</span>}
                            </Typography>
                            <Box>
                                <IconButton
                                    aria-label={expanded[idx] ? "Collapse" : "Expand"}
                                    onClick={() => handleToggleExpand(idx)}
                                    size="small"
                                >
                                    {expanded[idx] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                                <IconButton
                                    aria-label="delete"
                                    onClick={() => handleRemoveBuilding(idx)}
                                    disabled={buildings.length === 1}
                                    size="small"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Collapse in={expanded[idx]}>
                            <Box>
                                <TextField
                                    label="Building Name"
                                    name="name"
                                    value={b.building.name}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={!!errors?.[idx]?.name}
                                    helperText={errors?.[idx]?.name}
                                />
                                <TextField
                                    label="EAN 13"
                                    name="ean13"
                                    value={b.building.ean13}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={!!errors?.[idx]?.ean13}
                                    helperText={errors?.[idx]?.ean13}
                                />
                                <TextField
                                    label="Postal Code"
                                    name="postalCode"
                                    value={b.building.postalCode}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={!!errors?.[idx]?.postalCode}
                                    helperText={errors?.[idx]?.postalCode}
                                />
                                <TextField
                                    label="Street Number"
                                    name="streetNumber"
                                    value={b.building.streetNumber}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={!!errors?.[idx]?.streetNumber}
                                    helperText={errors?.[idx]?.streetNumber}
                                />
                                <TextField
                                    label="Street Number Addition"
                                    name="addition"
                                    value={b.building.addition}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Building Year"
                                    name="buildingYear"
                                    value={b.building.buildingYear}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Floor Size (m²)"
                                    name="floorSize"
                                    value={b.building.floorSize}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Isolation Grade"
                                    name="isolationGrade"
                                    value={b.building.isolationGrade}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Energy Label"
                                    name="energyLabel"
                                    value={b.building.energyLabel}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Usage"
                                    name="usage"
                                    value={b.building.usage}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Rental"
                                    name="rental"
                                    value={b.building.rental}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Occupancy Rate"
                                    name="occupancyRate"
                                    value={b.building.occupancyRate}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Climate Type"
                                    name="climateType"
                                    value={b.building.climateType}
                                    onChange={e => handleInputChange(idx, e)}
                                    fullWidth
                                    margin="normal"
                                />
                            </Box>
                        </Collapse>
                    </Paper>
                ))}
            </Stack>
            <Button
                variant="outlined"
                sx={{ mt: 3 }}
                onClick={handleAddBuilding}
            >
                Add Another Building
            </Button>
        </Box>
    );
};

export default BuildingStep;